import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import {
  FaClipboardList,
  FaCheckSquare,
  FaTimesCircle,
  FaChartBar,
} from "react-icons/fa";
import styles from "./Attendance.module.css";
import AxiosInstance, { TokenService } from "../../../services/Api";
import { useSelector } from "react-redux";

const localizer = momentLocalizer(moment);

const Attendance = () => {
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const roleId = Number(user?.role?.id);
  const master_id = clientData?.master_id || "";

  const { translations } = useSelector((state) => state.language);

  const [date, setDate] = useState(new Date());
  const [view, setView] = useState("month");
  const [loading, setLoading] = useState(false);

  // Student info from API response
  const [studentInfo, setStudentInfo] = useState({
    student_name: "",
    class_name: "",
    section_id: "",
  });

  // Stats
  const [yearStats, setYearStats] = useState({
    totalWorkingDays: 0,
    totalPresent: 0,
    totalAbsent: 0,
    attendancePercentage: "0",
  });
  const [monthStats, setMonthStats] = useState({
    totalWorkingDays: 0,
    totalPresent: 0,
    totalAbsent: 0,
    attendancePercentage: "0",
  });

  // Day-wise attendance for calendar
  const [dayWiseAttendance, setDayWiseAttendance] = useState([]);

  // Fetch on mount
  useEffect(() => {
    if (master_id) {
      fetchAttendanceData();
    }
  }, [master_id]);

  const fetchAttendanceData = async () => {
    setLoading(true);
    try {
      let apiUrl = "";
      let payload = {};

      if (roleId === 6) {
        // Parent
        apiUrl = `/attendance/parent/attendance`;
        payload = { parent_id: parseInt(master_id) };
      } else if (roleId === 4) {
        // Student
        apiUrl = `/attendance/student/attendance`;
        payload = { student_id: parseInt(master_id) };
      } else {
        setLoading(false);
        return;
      }

      const response = await AxiosInstance.post(apiUrl, payload);

      if (response?.data?.success || response?.data?.status) {
        const data = response.data.data || {};

        // Student info
        const student = data.student || {};
        setStudentInfo({
          student_name: student.student_name || "",
          class_name: student.class || "",
          section_id: student.section_id || "",
        });

        // Year summary
        const yearSummary = data.year_summary || {};
        setYearStats({
          totalWorkingDays: yearSummary.total_working_days || 0,
          totalPresent: yearSummary.total_present || "0",
          totalAbsent: yearSummary.total_absent || "0",
          attendancePercentage: yearSummary.attendance_percentage || "0",
        });

        // Month summary — find current month stats
        const monthSummary = data.month_summary || [];
        const currentMonth = moment().month() + 1;
        const currentYear = moment().year();
        const currentMonthData =
          monthSummary.find(
            (m) => m.month === currentMonth && m.year === currentYear,
          ) || {};
        setMonthStats({
          totalWorkingDays: currentMonthData.total_working_days || 0,
          totalPresent: currentMonthData.total_present || "0",
          totalAbsent: currentMonthData.total_absent || "0",
          attendancePercentage:
            currentMonthData.attendance_percentage || "0",
        });

        // Day-wise attendance
        setDayWiseAttendance(data.day_wise_attendance || []);
      }
    } catch (error) {
      console.error("Error fetching attendance data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Build calendar events from day_wise_attendance
  const events = useMemo(() => {
    return dayWiseAttendance.map((item) => {
      const eventDate = moment(item.attendance_date).toDate();
      let title = "";
      let color = "";

      if (item.attendance_status === 1) {
        title = translations.labels?.present || "Present";
        color = "#6fcf7c";
      } else if (item.student_leaveid && item.student_leaveid > 0) {
        title = translations.labels?.leave || "Leave";
        color = "#f5c842";
      } else {
        title = translations.labels?.absent || "Absent";
        color = "#f27474";
      }

      return {
        title,
        start: eventDate,
        end: eventDate,
        allDay: true,
        color,
      };
    });
  }, [dayWiseAttendance, translations]);

  // Navigate calendar
  const handleNavigate = useCallback((newDate) => {
    setDate(newDate);
  }, []);

  // Custom event styling
  const eventStyleGetter = useCallback((event) => {
    return {
      style: {
        backgroundColor: event.color,
        borderRadius: "0",
        opacity: 0.85,
        color: "#fff",
        border: "none",
        display: "block",
        fontSize: "14px",
        padding: "2px 4px",
      },
    };
  }, []);

  // Custom day cell styling — highlight today with blue
  const dayPropGetter = useCallback((dateCell) => {
    const isToday = moment(dateCell).isSame(moment(), "day");
    if (isToday) {
      return {
        style: {
          backgroundColor: "#7aa3d4",
        },
      };
    }
    return {};
  }, []);

  // Stat card config
  const statCards = [
    {
      row: "year",
      icon: <FaClipboardList />,
      iconClass: styles.iconBlue,
      value: yearStats.totalWorkingDays,
      label:
        translations.labels?.totalWorkingDaysYear ||
        "Total Working Days (Year)",
      barColor: "#7aa3d4",
      isPercentage: false,
    },
    {
      row: "year",
      icon: <FaCheckSquare />,
      iconClass: styles.iconGreen,
      value: yearStats.totalPresent,
      label: translations.labels?.totalPresentYear || "Total Present (Year)",
      barColor: "#f5c842",
      isPercentage: false,
    },
    {
      row: "year",
      icon: <FaTimesCircle />,
      iconClass: styles.iconRed,
      value: yearStats.totalAbsent,
      label: translations.labels?.totalAbsentYear || "Total Absent (Year)",
      barColor: "#ccc",
      isPercentage: false,
    },
    {
      row: "year",
      icon: <FaChartBar />,
      iconClass: styles.iconPurple,
      value: `${yearStats.attendancePercentage}%`,
      label:
        translations.labels?.attendancePercentageYear ||
        "Attendance Percentage (Year)",
      barColor: "#fa8316",
      isPercentage: true,
      percentValue: yearStats.attendancePercentage,
    },
    {
      row: "month",
      icon: <FaClipboardList />,
      iconClass: styles.iconBlue,
      value: monthStats.totalWorkingDays,
      label:
        translations.labels?.totalWorkingDaysMonth ||
        "Total Working Days (Month)",
      barColor: "#7aa3d4",
      isPercentage: false,
    },
    {
      row: "month",
      icon: <FaCheckSquare />,
      iconClass: styles.iconGreen,
      value: monthStats.totalPresent,
      label:
        translations.labels?.totalPresentMonth || "Total Present (Month)",
      barColor: "#f5c842",
      isPercentage: false,
    },
    {
      row: "month",
      icon: <FaTimesCircle />,
      iconClass: styles.iconRed,
      value: monthStats.totalAbsent,
      label: translations.labels?.totalAbsentMonth || "Total Absent (Month)",
      barColor: "#ccc",
      isPercentage: false,
    },
    {
      row: "month",
      icon: <FaChartBar />,
      iconClass: styles.iconPurple,
      value: `${monthStats.attendancePercentage}%`,
      label:
        translations.labels?.attendancePercentageMonth ||
        "Attendance Percentage (Month)",
      barColor: "#fa8316",
      isPercentage: true,
      percentValue: monthStats.attendancePercentage,
    },
  ];

  const yearCards = statCards.filter((c) => c.row === "year");
  const monthCards = statCards.filter((c) => c.row === "month");

  return (
    <div className={styles.container}>
      {/* Stat Cards - Year Row */}
      <div className={styles.statsGrid}>
        {yearCards.map((card, idx) => (
          <div key={`year-${idx}`} className={styles.statCard}>
            <div className={styles.statCardTop}>
              <div className={`${styles.statIcon} ${card.iconClass}`}>
                {card.icon}
              </div>
              <div className={styles.statContent}>
                <span className={styles.statValue}>{card.value}</span>
                <span className={styles.statLabel}>{card.label}</span>
              </div>
            </div>
            <div className={styles.statBar}>
              <div
                className={styles.statBarFill}
                style={{
                  backgroundColor: card.barColor,
                  width: card.isPercentage
                    ? `${card.percentValue}%`
                    : "50%",
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Stat Cards - Month Row */}
      <div className={styles.statsGrid}>
        {monthCards.map((card, idx) => (
          <div key={`month-${idx}`} className={styles.statCard}>
            <div className={styles.statCardTop}>
              <div className={`${styles.statIcon} ${card.iconClass}`}>
                {card.icon}
              </div>
              <div className={styles.statContent}>
                <span className={styles.statValue}>{card.value}</span>
                <span className={styles.statLabel}>{card.label}</span>
              </div>
            </div>
            <div className={styles.statBar}>
              <div
                className={styles.statBarFill}
                style={{
                  backgroundColor: card.barColor,
                  width: card.isPercentage
                    ? `${card.percentValue}%`
                    : "50%",
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Student Info Bar */}
      <div className={styles.studentInfoBar}>
        <span className={styles.studentName}>{studentInfo.student_name}</span>
        <span className={styles.studentDetail}>{studentInfo.class_name}</span>
        <span className={styles.studentDetail}>{studentInfo.section_id}</span>
      </div>

      {/* Calendar Section */}
      <div className={styles.calendarWrapper}>
        {/* Toolbar — matching Calendar.module.css design */}
        <div className={styles.toolbar}>
          <div className={styles.leftControls}>
            <button
              onClick={() =>
                handleNavigate(
                  moment(date).subtract(1, "year").toDate(),
                )
              }
            >
              <i className="fa-solid fa-angles-left" />
            </button>
            <button
              onClick={() =>
                handleNavigate(moment(date).add(1, "year").toDate())
              }
            >
              <i className="fa-solid fa-angles-right" />
            </button>
            <button
              onClick={() =>
                handleNavigate(
                  moment(date).subtract(1, "month").toDate(),
                )
              }
            >
              <i className="fa-solid fa-angle-left" />
            </button>
            <button
              onClick={() =>
                handleNavigate(moment(date).add(1, "month").toDate())
              }
            >
              <i className="fa-solid fa-angle-right" />
            </button>
            <button onClick={() => handleNavigate(new Date())}>
              {translations.labels?.today || "today"}
            </button>
          </div>

          <div className={styles.monthTitle}>
            {moment(date).format("MMMM YYYY").toUpperCase()}
          </div>

          <div className={styles.viewControls}>
            <button
              className={view === "month" ? styles.active : ""}
              onClick={() => setView("month")}
            >
              {translations.labels?.calendarMonth || "month"}
            </button>
            <button
              className={view === "week" ? styles.active : ""}
              onClick={() => setView("week")}
            >
              {translations.labels?.calendarWeek || "week"}
            </button>
            <button
              className={view === "day" ? styles.active : ""}
              onClick={() => setView("day")}
            >
              {translations.labels?.calendarDay || "day"}
            </button>
          </div>
        </div>

        {/* Calendar */}
        <Calendar
          localizer={localizer}
          date={date}
          view={view}
          onView={setView}
          onNavigate={handleNavigate}
          events={events}
          toolbar={false}
          startAccessor="start"
          endAccessor="end"
          className={styles.calendar}
          eventPropGetter={eventStyleGetter}
          dayPropGetter={dayPropGetter}
        />

        {/* Legend */}
        <div className={styles.legend}>
          <div className={styles.legendItem}>
            <span
              className={styles.legendColor}
              style={{ backgroundColor: "#6fcf7c" }}
            />
            <span className={styles.legendText}>
              {translations.labels?.present || "Present"}
            </span>
          </div>
          <div className={styles.legendItem}>
            <span
              className={styles.legendColor}
              style={{ backgroundColor: "#f27474" }}
            />
            <span className={styles.legendText}>
              {translations.labels?.absent || "Absent"}
            </span>
          </div>
          <div className={styles.legendItem}>
            <span
              className={styles.legendColor}
              style={{ backgroundColor: "#f5c842" }}
            />
            <span className={styles.legendText}>
              {translations.labels?.leave || "Leave"}
            </span>
          </div>
          <div className={styles.legendItem}>
            <span
              className={styles.legendColor}
              style={{ backgroundColor: "#7aa3d4" }}
            />
            <span className={styles.legendText}>
              {translations.labels?.todayDate || "Today Date"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Attendance;